import { OffersResponse } from "./OfferList";
import { Resource } from "../api/useFetch";
import { Offer, Location } from "./offer";
import { BookingType } from "BookingType";
import {
  MarketplaceOffersResponse,
  MarketplaceLocation,
} from "./marketplaceTypes";

/**
 * Transform Marketplace API response to JSON:API format
 * that matches the current Hades API structure
 */
export function transformMarketplaceToJsonApi(
  marketplaceResponse: MarketplaceOffersResponse,
): OffersResponse {
  const locationMap = new Map<string, Resource<Location>>();
  const offers: Array<Resource<Offer>> = [];

  // Handle case where items might be undefined or null
  if (!marketplaceResponse?.items) {
    console.warn(
      "[Marketplace Adapter] No items found in response:",
      marketplaceResponse,
    );
    return {
      data: [],
      included: [],
      meta: {
        record_count: 0,
        page_count: 0,
      },
    };
  }

  marketplaceResponse.items.forEach((marketplaceOffer) => {
    // Validate required fields at offer level
    if (!marketplaceOffer.id) {
      console.warn("[Marketplace Adapter] Skipping offer without ID");
      return;
    }

    if (
      typeof marketplaceOffer.price !== "number" ||
      isNaN(marketplaceOffer.price)
    ) {
      console.warn(
        `[Marketplace Adapter] Skipping offer ${marketplaceOffer.id}: invalid or missing price`,
      );
      return;
    }

    if (!marketplaceOffer.currency) {
      console.warn(
        `[Marketplace Adapter] Skipping offer ${marketplaceOffer.id}: missing currency`,
      );
      return;
    }

    if (!marketplaceOffer.vehicleClass) {
      console.warn(
        `[Marketplace Adapter] Skipping offer ${marketplaceOffer.id}: missing vehicleClass`,
      );
      return;
    }

    if (!marketplaceOffer.rides || marketplaceOffer.rides.length === 0) {
      console.warn(
        `[Marketplace Adapter] Skipping offer ${marketplaceOffer.id}: no rides`,
      );
      return;
    }

    const primaryRide = marketplaceOffer.rides[0];

    // Validate required fields at ride level
    if (!primaryRide.pickupTime) {
      console.warn(
        `[Marketplace Adapter] Skipping offer ${marketplaceOffer.id}: missing pickupTime`,
      );
      return;
    }

    if (!primaryRide.pickUpLocation) {
      console.warn(
        `[Marketplace Adapter] Skipping offer ${marketplaceOffer.id}: missing pickUpLocation`,
      );
      return;
    }

    if (
      !primaryRide.pickUpLocation.name ||
      typeof primaryRide.pickUpLocation.longitude !== "number" ||
      typeof primaryRide.pickUpLocation.latitude !== "number"
    ) {
      console.warn(
        `[Marketplace Adapter] Skipping offer ${marketplaceOffer.id}: invalid pickUpLocation data`,
      );
      return;
    }

    // Create unique location IDs based on name and coordinates
    const pickupLocationId = `mp-${primaryRide.pickUpLocation.name}-${primaryRide.pickUpLocation.longitude}-${primaryRide.pickUpLocation.latitude}`;
    const dropoffLocationId = primaryRide.dropOffLocation
      ? `mp-${primaryRide.dropOffLocation.name}-${primaryRide.dropOffLocation.longitude}-${primaryRide.dropOffLocation.latitude}`
      : undefined;

    addLocationToMap(locationMap, pickupLocationId, primaryRide.pickUpLocation);
    if (primaryRide.dropOffLocation && dropoffLocationId) {
      addLocationToMap(
        locationMap,
        dropoffLocationId,
        primaryRide.dropOffLocation,
      );
    }

    const offer: Resource<Offer> = {
      id: marketplaceOffer.id,
      attributes: {
        starts_at: primaryRide.pickupTime,
        ends_at: "", // Not provided by marketplace
        service_class: mapVehicleClass(marketplaceOffer.vehicleClass),
        price: marketplaceOffer.price.toFixed(2), // Convert number to string
        currency: marketplaceOffer.currency,
        distance: primaryRide.estimatedDistanceMeters || 0, // Keep in meters (Distance component handles conversion)
        duration:
          primaryRide.type === "hourly"
            ? primaryRide.durationMinutes
              ? primaryRide.durationMinutes * 60
              : undefined // Use durationMinutes for hourly rides
            : primaryRide.estimatedDurationMinutes
              ? primaryRide.estimatedDurationMinutes * 60
              : undefined, // Use estimatedDurationMinutes for transfer rides
        booking_type: mapBookingType(primaryRide.type),
        is_final_price: true, // Marketplace doesn't provide dynamic pricing
        next_price_change_in: 0, // Disable auto-refresh for marketplace offers
        special_requests: primaryRide.guestRequests || undefined,
        flight_number: primaryRide.flightNumber || undefined,
      },
      relationships: {
        pickup_location: { data: { id: pickupLocationId } },
        ...(dropoffLocationId && {
          dropoff_location: { data: { id: dropoffLocationId } },
        }),
      },
    };

    offers.push(offer);
  });

  return {
    data: offers,
    included: Array.from(locationMap.values()),
    meta: {
      record_count: marketplaceResponse.total_items,
      page_count: marketplaceResponse.total_pages,
    },
  };
}

function addLocationToMap(
  map: Map<string, Resource<Location>>,
  id: string,
  location: MarketplaceLocation,
) {
  if (!map.has(id)) {
    map.set(id, {
      id,
      attributes: {
        formatted_address_en: location.address,
      },
    });
  }
}

function mapVehicleClass(vehicleClass: string): string {
  // Map marketplace vehicle classes to Hades service classes
  const mapping: Record<string, string> = {
    business: "business",
    electric_class: "electric_class",
    first: "first",
    business_van: "business_van",
  };

  return mapping[vehicleClass] || vehicleClass.toLowerCase();
}

function mapBookingType(type: string): BookingType {
  // Map marketplace booking types to Hades booking types
  const typeMap: Record<string, BookingType> = {
    transfer: "transfer",
    hourly: "hourly",
  };

  return typeMap[type.toLowerCase()] || "transfer";
}
